export const chunkReloadMarkerKey = 'pickpro:chunk-reload';

const reloadWindowMs = 5 * 60 * 1000;

const chunkErrorPatterns = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'failed to load module script',
  'importing a module script failed',
  'unable to preload css',
];

const urlPattern = /(https?:\/\/\S+|\/\S+\.(?:js|mjs|css))/;

const messageOf = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return '';
};

export const isChunkLoadError = (error: unknown): boolean => {
  const message = messageOf(error).toLowerCase();
  return message !== '' && chunkErrorPatterns.some((pattern) => message.includes(pattern));
};

const readMarkerTime = (): number | null => {
  try {
    const raw = window.sessionStorage.getItem(chunkReloadMarkerKey);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { at } = parsed as { at?: unknown };
    return typeof at === 'number' && Number.isFinite(at) ? at : null;
  } catch {
    return null;
  }
};

const writeMarker = (at: number, url: string): void => {
  try {
    window.sessionStorage.setItem(chunkReloadMarkerKey, JSON.stringify({ at, url }));
  } catch {
    return;
  }
};

const reloadOnce = (url: string): boolean => {
  const now = Date.now();
  const last = readMarkerTime();
  if (last !== null && now - last >= 0 && now - last < reloadWindowMs) return false;
  writeMarker(now, url);
  window.location.reload();
  return true;
};

export const recoverFromChunkError = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) return false;
  return reloadOnce(urlPattern.exec(messageOf(error))?.[0] ?? window.location.href);
};

export const installChunkReload = (): (() => void) => {
  const onPreloadError = (event: Event): void => {
    event.preventDefault();
    const { payload } = event as Event & { payload?: unknown };
    const message = messageOf(payload);
    reloadOnce(urlPattern.exec(message)?.[0] ?? window.location.href);
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
  };
};
